import React, { useEffect } from 'react';
import classnames from 'classnames';
import { Button, Text } from '@moda/om';
import AmazonIcon from '@moda/icons/amazon-16';
import SocialFacebookIcon from '@moda/icons/social-facebook-16';
import { useSocialAuth, SocialAuthProvider, SocialAuthStep } from '../../hooks/useSocialAuth';
import { AuthAction, AuthPayloadSuccess } from '../../lib/authenticate';

import './SocialAuthButton.scss';

interface SocialAuthButtonProps extends React.HTMLAttributes<HTMLButtonElement> {
  action: AuthAction;
  provider: SocialAuthProvider;
  message?: string;
  showAuthModal?: () => void;
  hideAuthModal?: () => void;
  onAuth?: (authUser?: AuthPayloadSuccess) => void;
}

const ICONS = {
  [SocialAuthProvider.Amazon]: AmazonIcon,
  [SocialAuthProvider.Facebook]: SocialFacebookIcon
};

export const SocialAuthButton: React.FC<SocialAuthButtonProps> = ({
  action,
  provider,
  message,
  className,
  showAuthModal,
  hideAuthModal,
  onAuth,
  ...rest
}) => {
  const { step, handleLogin, renderModals } = useSocialAuth(provider, onAuth);
  const label = `${action} WITH ${provider}`;
  const AuthIcon = ICONS[provider];

  useEffect(() => {
    step === SocialAuthStep.Login ? showAuthModal?.() : hideAuthModal?.();
  }, [step, showAuthModal, hideAuthModal]);

  return (
    <>
      <Button
        aria-label={label.toLowerCase()}
        className={classnames(
          'SocialAuthButton',
          `SocialAuthButton--${provider.toLowerCase()}`,
          className
        )}
        onClick={handleLogin}
        type="button"
        {...rest}
        data-testid="socialAuthButton"
      >
        <span className="SocialAuthButton__icon">
          <AuthIcon height="1.75em" width="1.75em" />
        </span>
        {label}
      </Button>
      {message && <Text className="SocialAuthButton__message">{message}</Text>}
      {renderModals()}
    </>
  );
};
